import axios from "axios";
import { getYoutubeAccessToken, isYoutubeAccessTokenValid } from "./youtube-auth";
import { getGoogleApi, localStorageGet, localStorageSet } from "./window";

// videos?id=7lCDEYXw3mM
// &key=YOUR_API_KEY\n' + &part=snippet,contentDetails,statistics,status',
const youtubeAxios = axios.create({
  baseURL: 'https://www.googleapis.com/youtube/v3/',
  timeout: 1000
});

const parseYouTubeErrorObject = (error) => {
  // LOOK FOR 403 Quota Exceeded...
  if (error.domain == "youtube.playlist" && error.reason == "exceededRateLimit") {
    return "YouTube limits the number of playlists an account can create per day to about 10. You can wait till tomorrow or try another YouTube account."
  } else {
    return `${error.domain} ${error.reason} ${error.message}`;
  }
}

const handleYoutubeError = ( response = {}, action = '', alertBox = false ) => {
  let errorMessage = 'No Message From YouTube';

  if (response.result && response.result.error && response.result.error.errors) {
    errorMessage = response.result.error.errors.map(parseYouTubeErrorObject).concat('');
  } else if (response.data) {
    errorMessage = response.data.map(parseYouTubeErrorObject).concat('');
  }

  const displayMessage = `Failed to ${action}. Reason - ${errorMessage}`;

  if (alertBox) {
    alert(displayMessage);
  }

  return displayMessage;
};

const getChannel = () => {
  const googleApi = getGoogleApi();

  if (!isYoutubeAccessTokenValid()) {
    return Promise.reject({error: 'Not signed in'});
  }

  return googleApi.client.youtube.channels.list({
    part: 'snippet,status',
    mine: true
  }).then((response) => {
    const channel = response.result.items[0];
    const channelId = channel.id;
    const channelName = channel.snippet.title;
    // Indicates whether the channel data identifies a user that is already linked to either a YouTube username or a Google+ account.
    // A user that has one of these links already has a public YouTube identity, which is a prerequisite for several actions, such as uploading videos.
    const isLinked = channel.status.isLinked;
    const privacyStatus = channel.status.privacyStatus;

    return {
      channelId,
      channelName,
      isLinked,
      privacyStatus,
    };
  }).catch((response) => { handleYoutubeError(response, 'Load YouTube Channel', true) });
};

const getPlaylists = () => {
  const googleApi = getGoogleApi();

  if (!isYoutubeAccessTokenValid()) {
    return Promise.reject({error: 'Not signed in'});
  }

  return googleApi.client.youtube.playlists.list({
    part: 'snippet',
    mine: true,
    maxResults: 50
  }).then((response) => {
    const youtubePlaylists = response.result.items; // { snippet: { title: '' } }
  }).catch((response) => { handleYoutubeError(response, 'Load YouTube Playlists') });
};

const searchYoutube = (searchString, resolve) => {
  const googleApi = getGoogleApi();

  if (!isYoutubeAccessTokenValid()) {
    resolve({ error: { message: 'NOT_SIGNED_IN', searchString }});
    // return Promise.reject({error: 'Not signed in'});
  }

  googleApi.client.youtube.search.list({
    part: 'id',
    type: 'video',
    videoCategoryId: '10', // ID 10 is music, might consider removing if there are videos that aren't labelled in this category
    maxResults: 1,
    q: searchString
  }).then((response) => {
    const songs = response.result.items; // { snippet: { title: '' } }
    if (songs.length > 0) {
      const id = songs[0].id;
      const videoId = id.videoId;
      resolve({ success: { song: { videoId }, searchString }}); // return only what we need?
    } else {
      resolve({ error: { message: 'NO_RESULT', searchString } }); // return only what we need?
    }
  }).catch((response) => {
    const errorMessage = handleYoutubeError(response, 'Find Song on YouTube');
    resolve({ error: { message: errorMessage, searchString } }); // might want to add to a not added list with the id
  });
};

const createPlaylist = (title, isPrivate = false, resolve) => {
  const googleApi = getGoogleApi();

  if (!isYoutubeAccessTokenValid()) {
    resolve({ error: { message: 'NOT_SIGNED_IN', title }});
  }

  // Check title for bad characters

  let part = 'snippet';
  let resource = {
    snippet: {
      title: title || 'New Playlist Buddy Playlist',
      description: 'Playlist Buddy - Music videos, Spotify to YouTube conversion. Check it out at www.playlistbuddy.com'
    },
  };

  if (isPrivate) {
    part += ',status';

    resource.status = {
      privacyStatus: 'private'
    }
  }

  const request = googleApi.client.youtube.playlists.insert({ part, resource });

  request.execute((response) => {
    const result = response.result;

    if (result) {
      const playlistId = result.id; // result.snippet.title / description
      resolve({ success: { playlist: {id: playlistId}, title }}); // return only what we need?
    } else {
      const errorMessage = handleYoutubeError(response, 'Create Playlist', true);
      resolve({ error: { message: errorMessage, title } }); // might want to add to a not added list with the id
    }
  });
};

const insertIntoPlaylist = (videoId, playlistId, resolve) => {
  const googleApi = getGoogleApi();

  if (!isYoutubeAccessTokenValid()) {
    resolve({ error: { message: 'NOT_SIGNED_IN', videoId, playlistId }});
  }

  const resourceId = {
    kind: 'youtube#video',
    videoId
  }

  const request = googleApi.client.youtube.playlistItems.insert({
    part: 'snippet', // See if we can use just id to save on response bytes
    snippet: {
      playlistId,
      resourceId
    }
  });

  request.execute((response) => {
    const result = response.result;

    if (result) {
      resolve({ success: { song: result, videoId, playlistId }}); // result.id; // result.snippet.title
    } else {
      const errorMessage = handleYoutubeError(response, 'Add Song to Playlist');
      resolve({ error: { message: errorMessage, videoId, playlistId } }); // might want to add to a not added list with the id
    }
  });
};

export {
  getChannel,
  getPlaylists,
  searchYoutube,
  createPlaylist,
  insertIntoPlaylist
};



// WEBPACK FOOTER //
// ./src/lib/youtube-api.js